import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LayoutDashboard, Database, Rss, ShoppingBag, Plus, Search, CornerDownLeft, SlidersHorizontal, FolderTree, Activity, Rss as FeedIcon } from 'lucide-react';
import { api } from '../api/feedy';
import { cn } from '../lib/utils';

/**
 * ⌘K command palette — jump to pages, feeds, or run quick actions.
 */
export default function CommandPalette({ open, onClose }) {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [feeds, setFeeds] = useState([]);
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setQ(''); setActive(0);
      api.feeds.list().then(setFeeds).catch(() => {});
      setTimeout(() => inputRef.current?.focus(), 10);
    }
  }, [open]);

  const go = (path) => { onClose(); navigate(path); };

  const commands = useMemo(() => {
    const base = [
      { group: 'Navigate', icon: LayoutDashboard, label: 'Dashboard', kw: 'home overview', run: () => go('/dashboard') },
      { group: 'Navigate', icon: Rss, label: 'Feeds', kw: 'output google bol', run: () => go('/feeds') },
      { group: 'Navigate', icon: Database, label: 'Sources', kw: 'shopify csv import catalog', run: () => go('/sources') },
      { group: 'Navigate', icon: SlidersHorizontal, label: 'Master rules', kw: 'catalog rules edit filter', run: () => go('/master-rules') },
      { group: 'Navigate', icon: FolderTree, label: 'Categorisation', kw: 'google category gpc catalog', run: () => go('/categorisation') },
      { group: 'Navigate', icon: ShoppingBag, label: 'Channels', kw: 'spec fields config', run: () => go('/channels') },
      { group: 'Navigate', icon: Activity, label: 'Runs', kw: 'schedule pipeline history', run: () => go('/schedule') },
      { group: 'Actions', icon: Plus, label: 'New feed', kw: 'create', run: () => go('/feeds?new=1') },
      { group: 'Actions', icon: Plus, label: 'New source', kw: 'connect', run: () => go('/sources?new=1') },
    ];
    const feedCmds = feeds.map((f) => ({
      group: 'Feeds', icon: FeedIcon, label: f.name,
      hint: `${f.channel_display}${f.country ? ' · ' + f.country : ''}`,
      kw: `${f.name} ${f.channel}`, run: () => go(`/feeds/${f.id}`),
    }));
    const all = [...base, ...feedCmds];
    if (!q.trim()) return all;
    const needle = q.toLowerCase();
    return all.filter((c) => (c.label + ' ' + (c.kw || '')).toLowerCase().includes(needle));
  }, [feeds, q]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { setActive(0); }, [q]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, commands.length - 1)); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
      else if (e.key === 'Enter') { e.preventDefault(); commands[active]?.run(); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, commands, active, onClose]);

  if (!open) return null;

  let lastGroup = null;
  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center bg-foreground/20 p-4 pt-[12vh] backdrop-blur-[2px]" onMouseDown={onClose}>
      <div
        className="w-full max-w-xl overflow-hidden rounded-xl border border-border bg-popover shadow-pop animate-pop-in"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 border-b border-border px-3.5">
          <Search className="h-4 w-4 text-muted-foreground" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Jump to a page, feed, or action…"
            className="h-12 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          />
          <kbd className="rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">ESC</kbd>
        </div>
        <div className="max-h-[52vh] overflow-y-auto p-1.5">
          {commands.length === 0 && (
            <p className="px-3 py-8 text-center text-sm text-muted-foreground">No matches</p>
          )}
          {commands.map((c, i) => {
            const showGroup = c.group !== lastGroup;
            lastGroup = c.group;
            const Icon = c.icon;
            return (
              <React.Fragment key={`${c.group}-${c.label}-${i}`}>
                {showGroup && (
                  <div className="px-2.5 pb-1 pt-2 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{c.group}</div>
                )}
                <button
                  onMouseEnter={() => setActive(i)}
                  onClick={() => c.run()}
                  className={cn(
                    'flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-sm',
                    i === active ? 'bg-muted text-foreground' : 'text-foreground/90'
                  )}
                >
                  <Icon className="h-4 w-4 text-muted-foreground" />
                  <span className="flex-1 truncate">{c.label}</span>
                  {c.hint && <span className="truncate font-mono text-[11px] text-muted-foreground">{c.hint}</span>}
                  {i === active && <CornerDownLeft className="h-3.5 w-3.5 text-muted-foreground" />}
                </button>
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
}
